import React, { useState, useEffect, useCallback } from 'react';
import { getUsername, getAttendeesData, getPassCode, getTravelDetail, getCurrentUser, checkAdmin, checkApiError } from '../utils';

import '../css/traveldetails.css';
import { useNavigate } from 'react-router-dom';

const TravelDetails = ({ attendee }) => {
  const navigate = useNavigate();
  const isAdminLogin = checkAdmin() || false;
  const currentUser = getCurrentUser();
  attendee = attendee || currentUser;
  const allUserTravelDetails = getTravelDetail('all');
  const [dbID, setDbID] = useState(0);
  const [currTravelIndex, setCurrTravelIndex] = useState(-1);
  const [isEditable, setIsEditable] = useState(false);
  const [isAttendee, setIsAttendee] = useState(false);
  // --- State to track which section is editable ---
  const [editableSection, setEditableSection] = useState(null);

  // Helper function to format time for datetime-local input
  const formatTimeForInput = (hours, minutes) => {
    const now = new Date();
    now.setHours(hours, minutes, 0, 0); // Set desired time
    return now.toISOString().slice(0, 16);
  };

  const [travelDetails, setTravelDetails] = useState({
    flight: {
      inboundFlight: '',
      inboundTime: '',
      outboundFlight: '',
      outboundTime: '',
    },
    accommodation: {
      hotelName: '',
      reservationNumber: '',
      checkIn: formatTimeForInput(12, 0),
      checkOut: formatTimeForInput(11, 0),
    },
    transport: {
      arrivalBookingNumber: '',
      arrivalDateTime: '',
      arrivalPickUpLocation: '',
      arrivalDropUpLocation: '',
      deptBookingNumber: '',
      deptDateTime: '',
      deptPickUpLocation: '',
      deptDropUpLocation: '',
    },
  });

  // State to manage input validation errors
  const [errors, setErrors] = useState({});

  // --- Toggle edit mode for a specific section ---
  const toggleEditMode = (section) => {
    // Validation before saving
    if(isEditable){      
      if (Object.keys(errors).length > 0) {
        // Focus on the first error input
        document.querySelector("[name="+Object.keys(errors)[0].split('.')[1]+"]").focus();
        return;
      }
    }

    if (editableSection === section) {
      // If the same section is clicked again, close edit mode
      setEditableSection(null);
      setIsEditable(false); 
    } else {
      setEditableSection(section);
      setIsEditable(true);
    }

    if (isEditable) {
      // for edit mode is enbale and save button is visible
      saveTravelDetails();
    }
  };

  const checkFieldValidate = (section, name, value) => {
    let newErrors = { ...errors };
    let flag = false;
    switch (name) {
      case 'inboundFlight':
      case 'outboundFlight':       
      case 'hotelName':
      case 'arrivalPickUpLocation':
      case 'arrivalDropUpLocation':
      case 'deptPickUpLocation':
      case 'deptDropUpLocation':
      case 'reservationNumber':
      case 'arrivalBookingNumber':
      case 'deptBookingNumber':
        // Validate: Special characters not allowed except (.,'-&)
        flag = (!/^[a-zA-Z0-9\s-,.'&/]*$/.test(value));      
        break;
      default:
        break;
    }

    if (flag) {
      newErrors[`${section}.${name}`] = "Special characters not allowed except (. , ' - & /)";
    } else {
      delete newErrors[`${section}.${name}`];
    }

    setErrors(newErrors);
  }

  const handleInputChange = (e, section) => {
    const { name, value } = e.target;

    checkFieldValidate(section, name, value);   

    setTravelDetails({
      ...travelDetails,
      [section]: {
        ...travelDetails[section],
        [name]: value,
      },
    });
  };

  const getSingleUserData = useCallback(async (emailId) => {
    try {      
      //http://192.168.90.23:1440/api/Itinerary/getitinerarydetails?emailId=prabhat.shende@encora.com
      const response = await fetch(`${process.env.REACT_APP_API_URL}/Itinerary/getitinerarydetails?emailId=`+emailId, { // Replace with your API endpoint
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': "*/*",
          'Authorization': `Bearer ${getPassCode()}` // Assuming you need to pass the token
        }
      });
      
      if(checkApiError(response.status)){ navigate("/login"); return; }

      if (response.ok) {
        //console.log('Travel details saved successfully!');
        // Optionally: Display a success message to the user
        const data = await response.json();      
        return data;
      } else {
        console.error('Error saving travel details:', response.status);        
        // Optionally: Display an error message to the user
      }
    } catch (error) {
      console.error('Error saving travel details:', error);
      // Handle error, e.g., display an error message to the user
    }
  }, [navigate]);

  useEffect(() => {
    const fetchTravelDetails = async () => {
      try {
        const data = await getAttendeesData();
        const userEmail = getUsername();
        const currentUserData = data.find(attendee => attendee.email === userEmail);

        if (currentUserData && currentUserData.travelDetails) {
          for (let i in currentUserData.travelDetails) {
            const testData = currentUserData.travelDetails[i];
            for (let j in testData) {
              if (testData[j] === null) {
                testData[j] = "";
              }
            }
          }
          setTravelDetails(currentUserData.travelDetails);
        }
      } catch (error) {
        console.error("Error fetching travel details:", error);
      }
    };

    fetchTravelDetails();
  }, []);

  useEffect(() => {
    async function fetchData() {
      setIsEditable(false);
      // Reset to default state if attendee is null or has no travelDetails
      setTravelDetails({
        flight: {
          inboundFlight: '',
          inboundTime: '',
          outboundFlight: '',
          outboundTime: '',
        },
        accommodation: {
          hotelName: '',
          reservationNumber: '',
          checkIn: '',
          checkOut: '',
        },
        transport: {
          arrivalBookingNumber: '',
          arrivalDateTime: '',
          arrivalPickUpLocation: '',
          arrivalDropUpLocation: '',
          deptBookingNumber: '',
          deptDateTime: '',
          deptPickUpLocation: '',
          deptDropUpLocation: '',
        },
      });
      // Update travelDetails state when attendee prop changes
      
      if (attendee) {
        try{
          setCurrTravelIndex(-1);
          let travelDetails = await getSingleUserData(attendee.email);
          travelDetails = travelDetails ? travelDetails.iteneraryDetails : [];
          if (travelDetails.length > 0) {

            for (let i in travelDetails[0]) {
              if(typeof travelDetails[0][i] === 'object'){
                const testData = travelDetails[0][i];
                for (let j in testData) {
                  if (testData[j] === null) {
                    testData[j] = "";
                  }
                }
                travelDetails[0][i] = testData;
              }else{
                if (travelDetails[0][i] === null) {
                  travelDetails[0][i] = "";
                }
              }
            }
            
            setTravelDetails({
              flight: travelDetails[0].flightDetails,
              accommodation: travelDetails[0].accomodation,
              transport: travelDetails[0].groundTransportInformation
            });

            //setDbID(1);
            attendee.dbID = 1;
          } else {
            //setDbID(0);
            attendee.dbID = 0;
          }
          setIsAttendee(true);
        }catch(e){}
      }
    }
    fetchData();
  }, [attendee, getSingleUserData]);

  const saveTravelDetails = async () => {
    const userData = {
      id: attendee.dbID,
      attendee: attendee.name, // Assuming you want to save it by email
      emailId: attendee.email,
      ...travelDetails.flight,
      ...travelDetails.accommodation,
      ...travelDetails.transport
    };

    if(currTravelIndex === -1){
      allUserTravelDetails.push({
        id: allUserTravelDetails.length,
        emailId: userData.emailId,
        attendee: userData.attendee,
        flightDetails: travelDetails.flight,
        accomodation: travelDetails.accommodation,
        groundTransportInformation: travelDetails.transport
      });
    }else{
      allUserTravelDetails[currTravelIndex] = {
        id: allUserTravelDetails[currTravelIndex].id,
        emailId: userData.emailId,
        attendee: userData.attendee,
        flightDetails: travelDetails.flight,
        accomodation: travelDetails.accommodation,
        groundTransportInformation: travelDetails.transport
      };
    }

    for (let i in userData) {
      if (userData[i] === "") {
        userData[i] = null;
      }
    }

    if (dbID === 0) {
      postTraveData(userData);
    } else {
      putTravelData(userData);
    }
  };

  const postTraveData = async (data) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/Itinerary/saveitinerarydetails`, { // Replace with your API endpoint
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': "*/*",
          'Authorization': `Bearer ${getPassCode()}` // Assuming you need to pass the token
        },
        body: JSON.stringify(data),
      });

      if(checkApiError(response.status)){ navigate("/login"); return; }

      if (response.ok) {
        setDbID(1);
      } else {
        console.error('Error saving travel details:', response.status);
      }
    } catch (error) {
      console.error('Error saving travel details:', error);
    }
  }

  const putTravelData = async (data) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/Itinerary/updateitinerarydetails`, { // Replace with your API endpoint
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': "*/*",
          'Authorization': `Bearer ${getPassCode()}` // Assuming you need to pass the token
        },
        body: JSON.stringify(data),
      });

      if(checkApiError(response.status)){ navigate("/login"); return; }

      if (response.ok) {
      } else {
        console.error('Error saving travel details:', response.status);
      }
    } catch (error) {
      console.error('Error saving travel details:', error);
    }
  }

  return (
    <div className="travel-section w3-card-4 w3-margin-bottom-2em w3-round w3-padding w3-white">
      <div className=''>
        <h4 className='roboto-bold'>TRAVEL DETAILS <span> - {isAttendee ? attendee.name : currentUser.name}</span></h4>
        <div className="d-flex travel-details-container">
          <div className='travel-card w3-card-2 p-0 w3-margin-bottom w3-round'>
            {/* Flight Section */}
            <div className="flight-section w3-padding-16 border-bottom w3-padding">
              <h5 className="color-purple roboto-bold icon-title-btn d-flex align-items-center p-relative justify-content-space-btw">
                <div className='d-flex align-items-center'>
                  <span className='flight-icon'></span>
                  <span className="td-title">Flight</span>
                </div>
                <span className={`${ editableSection === 'flight' ? 'save-icon' : 'edit-icon'}`} onClick={()=> toggleEditMode('flight')}></span>
              </h5>
              <div className="form-grid">
                <label className="flight-icon-inbound color-black">Inbound Flight</label>
                {editableSection === 'flight' ? (
                  <div className='flight-details d-flex'>
                    <div>
                      <input
                        maxLength={15}
                        className={`w3-input w3-border ${errors['flight.inboundFlight'] ? 'error-border' : ''}`}
                        type="text"
                        name="inboundFlight"
                        value={travelDetails.flight.inboundFlight}
                        onChange={(e) => handleInputChange(e, 'flight')}
                      />
                      {errors['flight.inboundFlight'] && <div className="error-message">{errors['flight.inboundFlight']}</div>}
                    </div>
                    <div>
                      <input
                        className={`w3-input w3-border ${errors['flight.inboundTime'] ? 'error-border' : ''}`}
                        type="datetime-local"
                        name="inboundTime"
                        value={travelDetails.flight.inboundTime}
                        onChange={(e) => handleInputChange(e, 'flight')}
                        min={new Date().toISOString().slice(0, 16)}
                      />
                      {errors['flight.inboundTime'] && <div className="error-message">{errors['flight.inboundTime']}</div>}
                    </div>
                  </div>
                ) : (
                  <p>{((travelDetails.flight.inboundFlight !== "" && travelDetails.flight.inboundFlight) ? travelDetails.flight.inboundFlight : "") + ((travelDetails.flight.inboundTime !== "" && travelDetails.flight.inboundTime) ? " | " +travelDetails.flight.inboundTime.replace('T' ,' ') : "")}</p>
                )}

                <label className="flight-icon-outbound color-black">Outbound Flight</label>
                {editableSection === 'flight' ? (
                  <div className='flight-details d-flex'>
                    <div>
                      <input
                        maxLength={15}
                        className={`w3-input w3-border ${errors['flight.outboundFlight'] ? 'error-border' : ''}`}
                        type="text"
                        name="outboundFlight"
                        value={travelDetails.flight.outboundFlight}
                        onChange={(e) => handleInputChange(e, 'flight')}
                      />
                      {errors['flight.outboundFlight'] && <div className="error-message">{errors['flight.outboundFlight']}</div>}
                    </div>
                    <div>
                      <input
                        className={`w3-input w3-border ${errors['flight.outboundTime'] ? 'error-border' : ''}`}
                        type="datetime-local"
                        name="outboundTime"
                        value={travelDetails.flight.outboundTime}
                        onChange={(e) => handleInputChange(e, 'flight')}
                        min={new Date().toISOString().slice(0, 16)}
                      />
                      {errors['flight.outboundTime'] && <div className="error-message">{errors['flight.outboundTime']}</div>}
                    </div>
                  </div>
                ) : (
                  <p>{((travelDetails.flight.outboundFlight !== "" && travelDetails.flight.outboundFlight) ? travelDetails.flight.outboundFlight  : "") + ((travelDetails.flight.outboundTime !== "" && travelDetails.flight.outboundTime) ? " | "+ travelDetails.flight.outboundTime.replace('T' ,' ') : "")}</p>
                )}
              </div>
            </div>

            {/* Accommodation Section */}
            <div className="w3-padding-16 w3-padding">
              <h5 className="color-purple roboto-bold icon-title-btn d-flex align-items-center p-relative justify-content-space-btw">
                <div className='d-flex align-items-center'>
                  <span className='accommodation-icon'></span>
                  <span className="td-title">Accommodation</span>
                </div>
                {isAdminLogin ? (
                  <span className={`${ editableSection === 'accommodation' ? 'save-icon' : 'edit-icon'}`} onClick={()=> toggleEditMode('accommodation')}></span>
                ):(
                  <></>
                )}
              </h5>
              <div className="form-grid">
                <label className="color-black">Hotel Name</label>
                {editableSection === 'accommodation' ? (
                  <div>
                    <input
                      maxLength={100}
                      className={`w3-input w3-border ${errors['accommodation.hotelName'] ? 'error-border' : ''}`}
                      type="text"
                      name="hotelName"
                      value={travelDetails.accommodation.hotelName}
                      onChange={(e) => handleInputChange(e, 'accommodation')}
                    />
                    {errors['accommodation.hotelName'] && <div className="error-message">{errors['accommodation.hotelName']}</div>}
                  </div>
                ) : (
                  <p>{travelDetails.accommodation.hotelName || ""}</p>
                )}

                <label className="color-black">
                  Reservation Number</label>
                {editableSection === 'accommodation' ? (
                  <div>
                    <input
                      maxLength={15}
                      className={`w3-input w3-border ${errors['accommodation.reservationNumber'] ? 'error-border' : ''}`}
                      type="text"
                      name="reservationNumber"
                      value={travelDetails.accommodation.reservationNumber}
                      onChange={(e) => handleInputChange(e, 'accommodation')}
                    />
                    {errors['accommodation.reservationNumber'] && <div className="error-message">{errors['accommodation.reservationNumber']}</div>}
                  </div>
                ) : (
                  <p>{travelDetails.accommodation.reservationNumber || ""}</p>
                )}

                <label className="color-black">
                  Check-in</label>
                {editableSection === 'accommodation' ? (
                  <div>
                    <input
                      className={`w3-input w3-border ${errors['accommodation.checkIn'] ? 'error-border' : ''}`}
                      type="datetime-local"
                      name="checkIn"
                      value={travelDetails.accommodation.checkIn}
                      onChange={(e) => handleInputChange(e, 'accommodation')}
                      min={new Date().toISOString().slice(0, 16)}
                    />
                    {errors['accommodation.checkIn'] && <div className="error-message">{errors['accommodation.checkIn']}</div>}
                  </div>
                ) : (
                  <p>{(travelDetails.accommodation.checkIn && travelDetails.accommodation.checkIn.replace('T', ' ')) || ""}</p>
                )}

                <label className="color-black">
                  Check-out</label>
                {editableSection === 'accommodation' ? (
                  <div>
                    <input
                      className={`w3-input w3-border ${errors['accommodation.checkOut'] ? 'error-border' : ''}`}
                      type="datetime-local"
                      name="checkOut"
                      value={travelDetails.accommodation.checkOut}
                      onChange={(e) => handleInputChange(e, 'accommodation')}
                      min={new Date().toISOString().slice(0, 16)}
                    />
                    {errors['accommodation.checkOut'] && <div className="error-message">{errors['accommodation.checkOut']}</div>}
                  </div>
                ) : (
                  <p>{(travelDetails.accommodation.checkOut && travelDetails.accommodation.checkOut.replace('T', ' ')) || ""}</p>
                )}
              </div>
            </div>
          </div>
          <div className='travel-card w3-card-2 p-0 w3-margin-bottom w3-round w3-padding-16'>
            {/* Ground Transport Section */}
            <div className="cls-gt-section">
              <h5 className="color-purple roboto-bold icon-title-btn d-flex align-items-center p-relative justify-content-space-btw">
                <div className='d-flex align-items-center'>
                  <span className='car-icon'></span>
                  <span className="td-title">Ground Transport Information</span>
                </div>
                {isAdminLogin ? (
                  <span className={`${ editableSection === 'transport' ? 'save-icon' : 'edit-icon'}`} onClick={()=> toggleEditMode('transport')}></span>                  
                ):(
                  <></>
                )}
              </h5>
              <div className="w3-padding-16 border-bottom w3-padding">
                <h5 className="color-purple roboto-bold">Arrival to Venue</h5>
                <div className="form-grid">
                  <label className="color-black">
                    Booking Number</label>
                  {editableSection === 'transport' ? (
                    <div>
                      <input
                        maxLength={15}
                        className={`w3-input w3-border ${errors['transport.arrivalBookingNumber'] ? 'error-border' : ''}`}
                        type="text"
                        name="arrivalBookingNumber"
                        value={travelDetails.transport.arrivalBookingNumber}
                        onChange={(e) => handleInputChange(e, 'transport')}
                      />
                      {errors['transport.arrivalBookingNumber'] && <div className="error-message">{errors['transport.arrivalBookingNumber']}</div>}
                    </div>
                  ) : (
                    <p>{travelDetails.transport.arrivalBookingNumber || ""}</p>
                  )}

                  <label className="color-black">
                    Date / Time</label>
                  {editableSection === 'transport' ? (
                    <div>
                      <input
                        className={`w3-input w3-border ${errors['transport.arrivalDateTime'] ? 'error-border' : ''}`}
                        type="datetime-local"
                        name="arrivalDateTime"
                        value={travelDetails.transport.arrivalDateTime}
                        onChange={(e) => handleInputChange(e, 'transport')}
                        min={new Date().toISOString().slice(0, 16)}
                      />
                      {errors['transport.arrivalDateTime'] && <div className="error-message">{errors['transport.arrivalDateTime']}</div>}
                    </div>
                  ) : (
                    <p>{(travelDetails.transport.arrivalDateTime && travelDetails.transport.arrivalDateTime.replace('T', ' ')) || ""}</p>
                  )}

                  <label className="color-black">
                    Pick-up Location</label>
                  {editableSection === 'transport' ? (
                    <div>
                      <input
                        maxLength={100}
                        className={`w3-input w3-border ${errors['transport.arrivalPickUpLocation'] ? 'error-border' : ''}`}
                        type="text"
                        name="arrivalPickUpLocation"
                        value={travelDetails.transport.arrivalPickUpLocation}
                        onChange={(e) => handleInputChange(e, 'transport')}
                      />
                      {errors['transport.arrivalPickUpLocation'] && <div className="error-message">{errors['transport.arrivalPickUpLocation']}</div>}
                    </div>
                  ) : (
                    <p>{travelDetails.transport.arrivalPickUpLocation || ""}</p>
                  )}

                  <label className="color-black">
                    Drop-off Location</label>
                  {editableSection === 'transport' ? (
                    <div>
                      <input
                        maxLength={100}
                        className={`w3-input w3-border ${errors['transport.arrivalDropUpLocation'] ? 'error-border' : ''}`}
                        type="text"
                        name="arrivalDropUpLocation"
                        value={travelDetails.transport.arrivalDropUpLocation}
                        onChange={(e) => handleInputChange(e, 'transport')}
                      />
                      {errors['transport.arrivalDropUpLocation'] && <div className="error-message">{errors['transport.arrivalDropUpLocation']}</div>}
                    </div>
                  ) : (
                    <p>{travelDetails.transport.arrivalDropUpLocation || ""}</p>
                  )}
                </div>
              </div>
              <div className="w3-padding-16 w3-padding">
                <h5 className="color-purple roboto-bold">Departure from Venue</h5>
                <div className="form-grid">
                  <label className="color-black">
                    Booking Number</label>
                  {editableSection === 'transport' ? (
                    <div>
                      <input
                        maxLength={15}
                        className={`w3-input w3-border ${errors['transport.deptBookingNumber'] ? 'error-border' : ''}`}
                        type="text"
                        name="deptBookingNumber"
                        value={travelDetails.transport.deptBookingNumber}
                        onChange={(e) => handleInputChange(e, 'transport')}
                      />
                      {errors['transport.deptBookingNumber'] && <div className="error-message">{errors['transport.deptBookingNumber']}</div>}
                    </div>
                  ) : (
                    <p>{travelDetails.transport.deptBookingNumber || ""}</p>
                  )}

                  <label className="color-black">
                    Date / Time</label>
                  {editableSection === 'transport' ? (
                    <div>
                      <input
                        className={`w3-input w3-border ${errors['transport.deptDateTime'] ? 'error-border' : ''}`}
                        type="datetime-local"
                        name="deptDateTime"
                        value={travelDetails.transport.deptDateTime}
                        onChange={(e) => handleInputChange(e, 'transport')}
                        min={new Date().toISOString().slice(0, 16)}
                      />
                      {errors['transport.deptDateTime'] && <div className="error-message">{errors['transport.deptDateTime']}</div>}
                    </div>
                  ) : (
                    <p>{(travelDetails.transport.deptDateTime && travelDetails.transport.deptDateTime.replace('T', ' ')) || ""}</p>
                  )}

                  <label className="color-black">
                    Pick-up Location</label>
                  {editableSection === 'transport' ? (
                    <div>
                      <input
                        maxLength={100}
                        className={`w3-input w3-border ${errors['transport.deptPickUpLocation'] ? 'error-border' : ''}`}
                        type="text"
                        name="deptPickUpLocation"
                        value={travelDetails.transport.deptPickUpLocation}
                        onChange={(e) => handleInputChange(e, 'transport')}
                      />
                      {errors['transport.deptPickUpLocation'] && <div className="error-message">{errors['transport.deptPickUpLocation']}</div>}
                    </div>
                  ) : (
                    <p>{travelDetails.transport.deptPickUpLocation || ""}</p>
                  )}

                  <label className="color-black">
                    Drop-off Location</label>
                  {editableSection === 'transport' ? (
                    <div>
                      <input
                        maxLength={100}
                        className={`w3-input w3-border ${errors['transport.deptDropUpLocation'] ? 'error-border' : ''}`}
                        type="text"
                        name="deptDropUpLocation"
                        value={travelDetails.transport.deptDropUpLocation}
                        onChange={(e) => handleInputChange(e, 'transport')}
                      />
                      {errors['transport.deptDropUpLocation'] && <div className="error-message">{errors['transport.deptDropUpLocation']}</div>}
                    </div>
                  ) : (
                    <p>{travelDetails.transport.deptDropUpLocation || ""}</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TravelDetails;
